/**
 * Schedule 4D ↔ Task entity integration.
 * Uses the same task API as PlantTaskPage; links schedule rows to tasks by
 * type "schedule" and taskCode = material description.
 */
import {
  getAllTasks,
  createTask,
  updateTask,
} from "../../../../Project/plant/PlantTaskPage/TaskList/api";

const SCHEDULE_TASK_TYPE = "procurement";
const SCHEDULE_COST_CODE = "Procurement";

/**
 * Extract WBS code from mrqMaterial.wbs string.
 * e.g. "2.1 (Procure the Pipe material)" -> "2.1"
 */
export function extractWbsCode(wbs) {
  if (wbs == null || wbs === "") return "";
  const str = String(wbs).trim();
  // Format: "2.1 (description)" or "2.1.1 (description)"
  const beforeParen = str.split(/\s*\(/)[0].trim();
  return beforeParen || str;
}

/** Normalize description for matching: trim, collapse multiple spaces, consistent comma spacing */
function normalizeDescription(str) {
  if (str == null || str === "") return "";
  return String(str)
    .trim()
    .replace(/\s+/g, " ")
    .replace(/\s*,\s*/g, ", ");
}

/** Key without commas (for fallback match when material uses "W 12x40 A992" vs task "W 12x40, A992") */
function normalizedKeyNoCommas(str) {
  return normalizeDescription(str).replace(/,/g, "").replace(/\s+/g, " ").trim();
}

/**
 * Map normalized material description -> budgeted cost from plant estimation Summary rows.
 * Rows may be MCS.context shape ({ description, budgetedCost }) or raw Summary ({ MaterialDescription, budgetedCost, totalCost }).
 */
export function buildEstBudgetDescriptionMap(estData) {
  const map = new Map();
  (estData || []).forEach((row) => {
    const label =
      row?.MaterialDescription ?? row?.description ?? row?.materialDescription ?? "";
    const rawCost =
      row?.budgetedCost ?? row?.totalCost ?? row?.pmBudgetedCost ?? null;
    const n = Number(rawCost);
    if (!String(label).trim() || !Number.isFinite(n)) return;
    const k1 = normalizeDescription(label);
    const k2 = normalizedKeyNoCommas(label);
    if (k1) map.set(k1, n);
    if (k2 && k2 !== k1) map.set(k2, n);
  });
  return map;
}

/**
 * Sum estimation budgeted costs for each MRQ line matched by description (and nested material description).
 */
export function sumBudgetFromEstForMrqMaterials(mrqMaterials, estMap) {
  if (!(estMap instanceof Map) || !estMap.size || !Array.isArray(mrqMaterials)) {
    return null;
  }
  let sum = 0;
  let any = false;
  for (const mm of mrqMaterials) {
    const raw =
      mm?.description ??
      mm?.materials?.[0]?.description ??
      mm?.material?.description ??
      "";
    const k1 = normalizeDescription(raw);
    const k2 = normalizedKeyNoCommas(raw);
    const v =
      (k1 && estMap.get(k1)) ?? (k2 && estMap.get(k2)) ?? null;
    if (v != null && Number.isFinite(Number(v))) {
      sum += Number(v);
      any = true;
    }
  }
  return any ? sum : null;
}

/** Apply estimation-derived budgeted cost to procurement schedule rows (after MCS enrich). */
export function applyEstimationBudgetToScheduleRows(rows, estData) {
  const estMap = buildEstBudgetDescriptionMap(estData);
  if (!estMap.size) return rows || [];
  return (rows || []).map((row) => {
    const fromEst = sumBudgetFromEstForMrqMaterials(row.mrqMaterials, estMap);
    if (fromEst == null) return row;
    return { ...row, budgetedCost: fromEst };
  });
}

/** Get date/value from task supporting both camelCase and snake_case (API may return either) */
function getTaskDate(task, camelKey, snakeKey) {
  const v = task[camelKey] ?? task[snakeKey];
  return v ?? "";
}

/**
 * Calculate progress based on MRQ/PO/MRV status (same logic as columns)
 */
function calculateProgress(material) {
  const hasMrq = material.mrqMaterials?.some((m) => m?.mrq?.id);
  const hasPo = material.mrqMaterials?.some(
    (m) => m?.poId || m?.poMaterials?.[0]?.po?.id
  );
  const hasMrv =
    material.mrvMaterials?.[0]?.id ||
    material.mrqMaterials?.some((m) => m?.mrvMrqMaterials?.[0]?.mrv?.id);

  if (hasMrv) return 100;
  if (hasPo) return 60;
  if (hasMrq) return 30;
  return 0;
}

/**
 * Build map of taskCode/task description -> calculated progress for use in context (MIS/PMS).
 * Works with task-based rows: keys by both taskCode and task (description) for lookup flexibility.
 * Returns { map: Map, avgProgress: number | null }.
 */
export function buildProcurementProgressMapForContext(rows) {
  const map = new Map();
  (rows || []).forEach((row) => {
    const progress = calculateProgress(row);
    const taskCode = normalizeDescription(row.taskCode || "");
    const taskDesc = normalizeDescription(row.task ?? row.description ?? "") ||
      (row.taggedMaterial
        ? `${row.taggedMaterial?.component ?? ""} ${row.taggedMaterial?.size ?? ""}`.trim()
        : "");
    if (taskCode && taskCode.toLowerCase() !== "procurement") {
      map.set(taskCode, progress);
    }
    if (taskDesc && taskDesc !== "-") {
      map.set(taskDesc, progress);
    }
  });
  const values = Array.from(map.values()).filter(
    (v) => v != null && !Number.isNaN(Number(v))
  );
  const avgProgress =
    values.length > 0 ? values.reduce((a, b) => a + Number(b), 0) / values.length : null;
  return { map, avgProgress };
}

/**
 * Build maps of taskCode/task description -> budgetedCost and actualCost
 * for use in context (PMS 4D).
 * budgetedCost = task.estimatedTime (stored on the task entity).
 * actualCost = PO unit cost × quantity (same logic as getTotalCost in columns.jsx).
 * Returns { budgetedCostMap: Map, actualCostMap: Map }.
 */
export function buildProcurementCostMapForContext(rows) {
  const budgetedCostMap = new Map();
  const actualCostMap = new Map();

  const toNum = (v) => {
    if (v == null || v === "") return 0;
    const n = Number(v);
    return Number.isFinite(n) ? n : 0;
  };

  (rows || []).forEach((row) => {
    const taskCode = normalizeDescription(row.taskCode || "");
    const taskDesc =
      normalizeDescription(row.task ?? row.description ?? "") ||
      (row.taggedMaterial
        ? `${row.taggedMaterial?.component ?? ""} ${row.taggedMaterial?.size ?? ""}`.trim()
        : "");

    const budgetedCost = row.budgetedCost ?? row.estimatedTime;

    const quantity = toNum(row.quantity);
    let unitCost = 0;
    if (quantity > 0) {
      for (const mm of row.mrqMaterials || []) {
        for (const pm of mm.poMaterials || []) {
          const c = toNum(pm.cost);
          if (c > 0) { unitCost = c; break; }
        }
        if (unitCost > 0) break;
      }
      if (unitCost === 0) {
        unitCost = toNum(row.mrqMaterials?.[0]?.cost);
      }
    }
    const actualCost = unitCost > 0 ? unitCost * quantity : null;

    const setKeys = (key) => {
      if (!key || key.toLowerCase() === "procurement") return;
      if (budgetedCost != null && budgetedCost !== "" && Number.isFinite(Number(budgetedCost))) {
        budgetedCostMap.set(key, Number(budgetedCost));
      }
      if (actualCost != null && Number.isFinite(actualCost)) {
        actualCostMap.set(key, actualCost);
      }
    };

    setKeys(taskCode);
    setKeys(taskDesc);
  });

  return { budgetedCostMap, actualCostMap };
}

/**
 * Extract date string (YYYY-MM-DD) from MRQ for actual start.
 * Uses mrqDate first, then createdAt.
 */
function getMrqDate(mrq) {
  const raw = mrq?.mrqDate ?? mrq?.createdAt;
  if (!raw) return null;
  const str = typeof raw === "string" ? raw : (raw?.toISOString?.() ?? "");
  return str ? str.slice(0, 10) : null;
}

/**
 * Extract date string (YYYY-MM-DD) from MRV for actual end.
 * Uses mrvDate first, then createdAt.
 */
function getMrvDate(mrv) {
  const raw = mrv?.mrvDate ?? mrv?.createdAt;
  if (!raw) return null;
  const str = typeof raw === "string" ? raw : (raw?.toISOString?.() ?? "");
  return str ? str.slice(0, 10) : null;
}

/**
 * Build maps of taskCode/task description -> actual start (MRQ date) and actual end (MRV date).
 * For procurement: actual start = earliest MRQ date, actual end = latest MRV date.
 * Returns { actualStartMap: Map, actualEndMap: Map }.
 */
export function buildProcurementDatesMapForContext(rows) {
  const actualStartMap = new Map();
  const actualEndMap = new Map();

  (rows || []).forEach((row) => {
    const taskCode = normalizeDescription(row.taskCode || "");
    const taskDesc =
      normalizeDescription(row.task ?? row.description ?? "") ||
      (row.taggedMaterial
        ? `${row.taggedMaterial?.component ?? ""} ${row.taggedMaterial?.size ?? ""}`.trim()
        : "");

    let earliestMrqDate = null;
    (row.mrqMaterials || []).forEach((mm) => {
      const d = getMrqDate(mm.mrq ?? mm);
      if (d && (!earliestMrqDate || d < earliestMrqDate)) earliestMrqDate = d;
    });

    let latestMrvDate = null;
    (row.mrvMaterials || []).forEach((m) => {
      const d = getMrvDate(m);
      if (d && (!latestMrvDate || d > latestMrvDate)) latestMrvDate = d;
    });
    (row.mrqMaterials || []).forEach((mm) => {
      (mm.mrvMrqMaterials || []).forEach((mrm) => {
        const d = getMrvDate(mrm.mrv);
        if (d && (!latestMrvDate || d > latestMrvDate)) latestMrvDate = d;
      });
    });

    const actualStart = earliestMrqDate || null;
    const actualEnd = latestMrvDate || null;

    if (taskCode && taskCode.toLowerCase() !== "procurement") {
      if (actualStart) actualStartMap.set(taskCode, actualStart);
      if (actualEnd) actualEndMap.set(taskCode, actualEnd);
    }
    if (taskDesc && taskDesc !== "-") {
      if (actualStart) actualStartMap.set(taskDesc, actualStart);
      if (actualEnd) actualEndMap.set(taskDesc, actualEnd);
    }
  });

  return { actualStartMap, actualEndMap };
}

/**
 * Fetch procurement schedule tasks for Schedule4D. Returns only tasks with a real WBS code
 * (e.g. "2.1") - excludes placeholder tasks where taskCode is "Procurement" or empty.
 */
export async function getScheduleTasks({ plantId, accessToken }) {
  if (!plantId) return [];
  try {
    const tasks = await getAllTasks({ plantId, accessToken });
    const scheduleTasks = (tasks || []).filter((t) => {
      const typeMatch =
        (t.type || "").toLowerCase() === SCHEDULE_TASK_TYPE ||
        (t.costCode || "").toLowerCase() === SCHEDULE_COST_CODE;
      if (!typeMatch) return false;
      const code = String(t.taskCode || "").trim();
      // Exclude placeholder tasks: taskCode "Procurement" or empty (material-description rows)
      if (!code || code.toLowerCase() === "procurement") return false;
      return true;
    });
    return scheduleTasks;
  } catch (error) {
    console.error("[Schedule4D] Error in getScheduleTasks:", error);
    throw error;
  }
}

/** Unique MRQ material rows when merging WBS + taskId lookups (same MRQ may appear twice). */
function mergeMrqMaterialLists(a, b) {
  const seen = new Set();
  const out = [];
  for (const m of [...(a || []), ...(b || [])]) {
    const k =
      m?.id != null
        ? `id:${m.id}`
        : m?.mrq?.id != null
          ? `mrq:${m.mrq.id}`
          : `row:${out.length}`;
    if (seen.has(k)) continue;
    seen.add(k);
    out.push(m);
  }
  return out;
}

function mergeMrvMaterialLists(a, b) {
  const seen = new Set();
  const out = [];
  for (const m of [...(a || []), ...(b || [])]) {
    const id = m?.id;
    if (id != null) {
      if (seen.has(id)) continue;
      seen.add(id);
    }
    out.push(m);
  }
  return out;
}

/** Comma-separated distinct MRQ numbers from enriched task rows (PMS 4D / modals). */
export function formatMrqNumbersFromTask(task) {
  const nums = new Set();
  (task?.mrqMaterials || []).forEach((m) => {
    const n = m?.mrq?.mrqNumber;
    if (n != null && String(n).trim() !== "") nums.add(String(n).trim());
  });
  return Array.from(nums).join(", ");
}

/**
 * Enrich procurement tasks with MCS status for the Schedule4D UI.
 * mcsBundle: Map (legacy) or { byTaskCode, byTaskId } from getMcsStatusForTasks.
 */
export function enrichTasksWithMcsStatus(tasks, mcsBundle) {
  const taskList = Array.isArray(tasks) ? tasks : [];
  const statusMap =
    mcsBundle instanceof Map
      ? mcsBundle
      : mcsBundle?.byTaskCode instanceof Map
        ? mcsBundle.byTaskCode
        : new Map();
  const byTaskId =
    mcsBundle instanceof Map
      ? new Map()
      : mcsBundle?.byTaskId instanceof Map
        ? mcsBundle.byTaskId
        : new Map();

  return taskList.map((task) => {
    const taskCode = String(task.taskCode || "").trim();
    // Support "code-groupName" format: match by full taskCode or code part for MRQ WBS lookup
    const codePart = taskCode.includes("-") ? taskCode.split("-")[0].trim() : taskCode;
    const fromCode =
      statusMap.get(taskCode) ||
      (codePart ? statusMap.get(codePart) : null) ||
      { mrqMaterials: [], mrvMaterials: [] };
    const fromId = task.id != null ? byTaskId.get(String(task.id)) : null;

    let mrqMaterials = mergeMrqMaterialLists(
      fromCode.mrqMaterials,
      fromId?.mrqMaterials
    );
    let mrvMaterials = mergeMrvMaterialLists(
      fromCode.mrvMaterials,
      fromId?.mrvMaterials
    );

    const baselineStart = getTaskDate(task, "startDate", "start_date");
    const baselineEnd = getTaskDate(task, "endDate", "end_date");
    const revisedStart = getTaskDate(task, "revisedStart", "revised_start");
    const revisedEnd = getTaskDate(task, "revisedEnd", "revised_end");
    const estimatedTime = task.estimatedTime ?? task.estimated_time;
    const budgetedCost =
      estimatedTime != null && estimatedTime !== ""
        ? Number(estimatedTime)
        : "";

    const quantity = mrqMaterials.reduce(
      (sum, mm) => sum + (Number(mm.procuredQty ?? mm.quantity) || 0),
      0
    );

    return {
      ...task,
      scheduleTaskId: task.id,
      baselineStart: baselineStart ?? "",
      baselineEnd: baselineEnd ?? "",
      revisedStart: revisedStart ?? "",
      revisedEnd: revisedEnd ?? "",
      budgetedCost: Number.isFinite(budgetedCost) ? budgetedCost : "",
      description: task.task ?? task.name ?? "",
      mrqMaterials,
      mrvMaterials,
      quantity,
    };
  });
}

/**
 * @deprecated Use enrichTasksWithMcsStatus for task-based rows. Kept for MIS fallback.
 */
export function mergeScheduleTasksIntoMaterials(materials, scheduleTasks) {
  const byTaskCode = new Map();
  (scheduleTasks || []).forEach((t) => {
    const code = String(t.taskCode || "").trim();
    if (code && code.toLowerCase() !== "procurement") {
      byTaskCode.set(code, t);
    }
  });

  const byTaskName = new Map();
  const byTaskNameNoCommas = new Map();
  (scheduleTasks || []).forEach((t) => {
    const taskName = normalizeDescription(t.task || "");
    if (taskName) {
      byTaskName.set(taskName, t);
      const noCommas = normalizedKeyNoCommas(t.task || "");
      if (noCommas) byTaskNameNoCommas.set(noCommas, t);
    }
  });

  return (materials || []).map((row) => {
    let task = null;
    const wbsCodes = (row.mrqMaterials || [])
      .map((mm) => extractWbsCode(mm.wbs))
      .filter((c) => c);
    for (const code of wbsCodes) {
      task = byTaskCode.get(code);
      if (task) break;
    }
    if (!task) {
      const rawDescription = row.description ||
        (row.taggedMaterial
          ? `${row.taggedMaterial?.component ?? ""} ${row.taggedMaterial?.size ?? ""}`.trim()
          : "");
      const description = rawDescription ? normalizeDescription(rawDescription) : "-";
      task = byTaskName.get(description);
      if (!task && description !== "-") {
        task = byTaskNameNoCommas.get(normalizedKeyNoCommas(rawDescription));
      }
    }

    if (!task) {
      return {
        ...row,
        scheduleTaskId: row.scheduleTaskId ?? null,
        baselineStart: row.baselineStart ?? "",
        baselineEnd: row.baselineEnd ?? "",
        revisedStart: row.revisedStart ?? "",
        revisedEnd: row.revisedEnd ?? "",
        budgetedCost: row.budgetedCost ?? "",
      };
    }

    const baselineStart = getTaskDate(task, "startDate", "start_date") ?? row.baselineStart ?? "";
    const baselineEnd = getTaskDate(task, "endDate", "end_date") ?? row.baselineEnd ?? "";
    const revisedStart = getTaskDate(task, "revisedStart", "revised_start") ?? row.revisedStart ?? "";
    const revisedEnd = getTaskDate(task, "revisedEnd", "revised_end") ?? row.revisedEnd ?? "";
    const estimatedTime = task.estimatedTime ?? task.estimated_time;
    const budgetedCost =
      estimatedTime != null && estimatedTime !== ""
        ? Number(estimatedTime)
        : (row.budgetedCost ?? "");

    return {
      ...row,
      scheduleTaskId: task.id,
      baselineStart,
      baselineEnd,
      revisedStart,
      revisedEnd,
      budgetedCost: Number.isFinite(budgetedCost) ? budgetedCost : (row.budgetedCost ?? ""),
    };
  });
}

/**
 * Create a task entity for a schedule row (one task per consolidated material).
 * type = "procurement", costCode = "procurement", task = description (no prefix).
 * Budgeted cost is stored in estimatedTime.
 * task_members must be a string (logged-in user name).
 */
export async function createScheduleTask({
  plantId,
  accessToken,
  userName,
  description,
  taskCode: taskCodeParam,
  baselineStart,
  baselineEnd,
  revisedStart,
  revisedEnd,
  budgetedCost,
  progress = 0,
}) {
  const estimatedTime =
    budgetedCost != null && budgetedCost !== ""
      ? Number(budgetedCost)
      : 0;
  const taskPayload = {
    task: description || "Material", // Material description - used for matching
    taskCode: taskCodeParam || SCHEDULE_COST_CODE, // WBS code (e.g. "2.1") or "Procurement"
    type: SCHEDULE_TASK_TYPE,
    costCode: SCHEDULE_COST_CODE, // costCode is "Procurement"
    priority: "Medium",
    status: "Not Started",
    task_members: typeof userName === "string" ? userName : "",
    startDate: baselineStart || null,
    endDate: baselineEnd || null,
    revisedStart: revisedStart || null,
    revisedEnd: revisedEnd || null,
    estimatedTime: Number.isFinite(estimatedTime) ? estimatedTime : 0,
    progress: Number.isFinite(progress) ? progress : 0,
  };
  const res = await createTask({
    task: taskPayload,
    plantId,
    accessToken,
  });
  const task = res?.data ?? res;
  return task;
}

/**
 * Update an existing schedule task with new dates, budgeted cost (estimatedTime), and progress.
 */
export async function updateScheduleTask({
  accessToken,
  taskId,
  task: taskDescription,
  taskCode: taskCodeParam,
  baselineStart,
  baselineEnd,
  revisedStart,
  revisedEnd,
  budgetedCost,
  progress,
}) {
  const estimatedTime =
    budgetedCost != null &&
    budgetedCost !== "" &&
    Number.isFinite(Number(budgetedCost))
      ? Number(budgetedCost)
      : 0;
  const taskPayload = {
    id: taskId,
    taskCode: taskCodeParam || SCHEDULE_COST_CODE,
    costCode: SCHEDULE_COST_CODE,
    startDate: baselineStart || null,
    endDate: baselineEnd || null,
    revisedStart: revisedStart || null,
    revisedEnd: revisedEnd || null,
    estimatedTime,
  };
  if (taskDescription != null && taskDescription !== "") {
    taskPayload.task = taskDescription;
  }
  if (progress != null) {
    taskPayload.progress = Number.isFinite(Number(progress)) ? Number(progress) : 0;
  }
  console.log("[Schedule4D] Updating task, taskId:", taskId, "estimatedTime:", estimatedTime);
  await updateTask({
    task: taskPayload,
    accessToken,
  });
}

/**
 * Update progress for all schedule tasks based on MRQ/PO/MRV status from attached materials.
 * scheduleRows are task-based rows (each has scheduleTaskId and mrqMaterials).
 */
export async function updateScheduleTasksProgress({
  plantId,
  accessToken,
  scheduleRows,
}) {
  if (!plantId || !Array.isArray(scheduleRows)) return;

  const updates = [];

  scheduleRows.forEach((row) => {
    const taskId = row.scheduleTaskId ?? row.id;
    if (!taskId) return;
    const progress = calculateProgress(row);
    const currentProgress = row.progress ?? row.totalProgress;
    if (currentProgress !== progress) {
      updates.push(
        updateScheduleTask({
          accessToken,
          taskId,
          taskCode: row.taskCode,
          baselineStart: row.baselineStart ?? row.startDate,
          baselineEnd: row.baselineEnd ?? row.endDate,
          revisedStart: row.revisedStart ?? row.revised_start,
          revisedEnd: row.revisedEnd ?? row.revised_end,
          budgetedCost: row.budgetedCost ?? row.estimatedTime,
          progress,
        }).catch((err) => {
          console.error(`[Schedule4D] Failed to update progress for task ${taskId}:`, err);
        })
      );
    }
  });

  const results = await Promise.allSettled(updates);
  const successCount = results.filter((r) => r.status === "fulfilled").length;
  console.log(`[Schedule4D] Updated ${updates.length} tasks (${successCount} succeeded)`);
}
